
import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, from } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { LoginPopupComponent } from '../login/login-popup/login-popup.component';
import { CommonService } from '../common.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { ConfirmDialogComponent } from '../common/confirm-dialog/confirm-dialog.component';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private readonly refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private token = '';

    constructor(public authService: AuthService, private readonly commonService: CommonService,
        private readonly dialog: MatDialog, private readonly snackBar: MatSnackBar, private readonly ouathService: OAuthService) {

    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.commonService.ProjectLSData['isGuestLogin']) {
            this.token = this.commonService.ProjectLSData['useAccessToken'] ? this.ouathService.getAccessToken() : this.ouathService.getIdToken();
        } else {
            this.token = this.authService.getAccessToken();
        }
        if ((request.url.indexOf("user/login") > 0)) {
            request = this.setHeader(request);
        }
        else if (!request.url.includes('/schema.json') ||
            !request.url.includes('public-access/') ||
            !request.url.includes('forgetUserPassword?userId') ||
            !request.url.includes('verifyCode') ||
            !request.url.includes('resetPassword') ||
            !request.url.includes('session/config')) {
            if (this.token) {
                request = this.addToken(request, this.token);
            } else if (request.url.includes('/actuator/license/upload')) {
                // intentionally skip default headers for license upload
            }
            else {
                request = this.setHeader(request);
            }
        }
        return next.handle(request).pipe(catchError(error => {

            if (error instanceof HttpErrorResponse && error.status == 401) {
                if ((request.url.indexOf('/user/login') < 0) && !request.url.includes("user/logout")) {
                    return this.handle401Error(request, next);
                }
            } else if (error.status == 400 && error.error.error === 'invalid_grant') {
                this.handl400Error(error);

            } else if (error instanceof HttpErrorResponse && error.status == 400 && request.url.includes('/user/logout')) {
                return throwError(error);
            } else {
                if (request.url.indexOf('/token/refresh') != -1) {
                    this.isRefreshing = false;
                }
                if (error instanceof HttpErrorResponse && ((error.status === 500 && !(request.url.includes("/schema?query"))) || (error.status === 409) || ((error.status === 400) && !(request.url.includes("/token/refresh"))))) {
                    if (error?.error?.message) {
                        this.commonService.showToastMsg(error.error.message, 'error')
                    }

                }
            }
            return throwError(error);
        }));
    }
    private addToken(request: HttpRequest<any>, token: string) {
        return request.clone({
            setHeaders: {
                'Authorization': `Bearer ${token}`
            }
        });
    }

    private setHeader(request: HttpRequest<any>) {

        if (this.commonService.ProjectLSData['isGuestLogin'] && request.url.includes('/token')) {
            return request.clone({
                setHeaders: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                }
            });
        }
        return request.clone({
            setHeaders: {
                'Content-Type': 'application/json; ',
            }
        });
    }

    private handl400Error(err: any) {

        if (!this.isRefreshing) {
            this.isRefreshing = true;
            if (err.error.error === 'invalid_grant') {
                const dialogConfig = new MatDialogConfig();
                dialogConfig.width = "30%";
                dialogConfig.panelClass = "confirmDialog";
                dialogConfig.data = {
                    headerTitle: "Alert",
                    confirmMsg: "Session expired. Login again to continue.",
                    cancelButtonText: "Cancel",
                    confirmButtonText: "Relogin",
                    confirmActionData: {
                        currentPage: 'session_expired',
                        that: this
                    },
                    hideCancelButton: true
                };
                this.commonService.ProjectLSData['isSessionExpired'] = true;
                this.commonService.setProjectLSData();
                this.dialog.open(ConfirmDialogComponent, dialogConfig);
            }
        }
        return throwError(err);
    }


    private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            const refresToken = this.commonService.ProjectLSData['isGuestLogin'] ? from(this.ouathService.getRefreshToken()) : this.authService.refreshToken();
            return refresToken.pipe(
                switchMap((token: any) => {
                    this.isRefreshing = false;
                    let newAccessToken: any;
                    if (this.commonService.ProjectLSData['isGuestLogin']) {
                        newAccessToken = this.commonService.ProjectLSData['useAccessToken'] ? this.ouathService.getAccessToken() : this.ouathService.getIdToken();
                    } else {
                        newAccessToken = this.authService.getAccessToken();
                    }
                    this.refreshTokenSubject.next(newAccessToken);
                    return next.handle(this.addToken(request, newAccessToken));
                }),
                catchError((err: any) => {
                    this.isRefreshing = false;
                    if ((!this.commonService.ProjectLSData['isGuestLogin'] && (err.status === 400)) || (this.commonService.ProjectLSData['isGuestLogin'] && (err.status === 401))) {
                        const dialogConfig = new MatDialogConfig();
                        dialogConfig.disableClose = true;
                        dialogConfig.panelClass = 'tokenExpLoginPopupPanel';
                        dialogConfig.backdropClass = 'clsLoginPopupBackDrop';
                        dialogConfig.data = {
                            headerTitle: "Login",
                            confirmMsg: "Session expired. Login again to continue.",
                            cancelButtonText: "Cancel",
                            confirmButtonText: "Yes",
                            isAccessTokenExpired: true,
                            location:'interceptor'
                        };
                        const dialogref = this.dialog.open(LoginPopupComponent, dialogConfig);
                        return dialogref.afterClosed().pipe(switchMap((result: any) => {
                            if (result?.loginStatus == "success") {
                                this.refreshTokenSubject.next(this.authService.getAccessToken());
                                return next.handle(this.addToken(request, this.authService.getAccessToken()));
                            }
                            return throwError(err);
                        }));
                    }
                    return throwError(err);
                }));
        } else {
            return this.refreshTokenSubject.pipe(
                filter(token => token != null),
                take(1),
                switchMap(access_token => {
                    return next.handle(this.addToken(request, access_token));
                }));
        }
    }
}